import React, { useMemo } from 'react'
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import type { CurvaAggregate } from '../../hooks/useCurvaS'

interface SCurveChartProps {
  data: CurvaAggregate[]
  title?: string
  showDeviation?: boolean
  height?: number
}

export const SCurveChart: React.FC<SCurveChartProps> = ({
  data,
  title = 'Kurva S Timeline',
  showDeviation = true,
  height = 400
}) => {
  // Transform data untuk chart
  const chartData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      // Normalize ke percentage (0-100)
      planned: parseFloat(item.plannedCumulativeWeight.toFixed(2)),
      actual: item.actualCumulativeWeight
        ? parseFloat(item.actualCumulativeWeight.toFixed(2))
        : null,
      deviation: item.deviation ? parseFloat(item.deviation.toFixed(2)) : null,
      // Use periodLabel for X axis (1-7 Juni, 8-14 Juni, etc)
      name: item.periodLabel
    }))
  }, [data])

  // Calculate max deviation untuk axis scaling
  const maxDeviation = useMemo(() => {
    const deviations = chartData
      .map((d) => Math.abs(d.deviation || 0))
      .filter((d) => d > 0)
    return Math.max(...deviations, 5)
  }, [chartData])

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg text-xs">
          <p className="font-semibold text-gray-700">{data.name}</p>
          <p className="text-blue-600">
            Rencana: {data.planned}%
          </p>
          {data.actual !== null && (
            <>
              <p className="text-green-600">
                Realisasi: {data.actual}%
              </p>
              {data.deviation !== null && (
                <p className={`font-semibold ${data.deviation >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  Deviasi: {data.deviation > 0 ? '+' : ''}{data.deviation}%
                </p>
              )}
            </>
          )}
        </div>
      )
    }
    return null
  }

  if (chartData.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-50 rounded border-2 border-dashed border-gray-300">
        <p className="text-gray-500">No data available. Generate time periods first.</p>
      </div>
    )
  }

  return (
    <div className="w-full">
      {title && <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>}
      
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            height={100}
            tick={{ fontSize: 12 }}
            interval={Math.ceil(chartData.length / 8) - 1} // Show ~8 labels max
          />
          
          {/* Left Y axis untuk percentage (0-100) */}
          <YAxis
            yAxisId="left"
            domain={[0, 100]}
            label={{ value: 'Bobot (%)', angle: -90, position: 'insideLeft', offset: 10 }}
            tick={{ fontSize: 12 }}
          />
          
          {/* Right Y axis untuk deviation (optional) */}
          {showDeviation && (
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={[-maxDeviation, maxDeviation]}
              label={{ value: 'Deviasi (%)', angle: 90, position: 'insideRight', offset: 10 }}
              tick={{ fontSize: 12 }}
            />
          )}

          <Tooltip content={<CustomTooltip />} />
          
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            verticalAlign="top"
            height={30}
          />

          {/* Planned line (Blue) */}
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="planned"
            stroke="#3b82f6"
            strokeWidth={2.5}
            dot={false}
            isAnimationActive={false}
            name="Bobot Rencana"
          />

          {/* Actual line (Green) - only shows if data exists */}
          {chartData.some((d) => d.actual !== null) && (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="actual"
              stroke="#10b981"
              strokeWidth={2.5}
              dot={false}
              isAnimationActive={false}
              name="Bobot Realisasi"
              connectNulls
            />
          )}

          {/* Deviation bar (Orange) - only if showing deviasi */}
          {showDeviation && (
            <Bar
              yAxisId="right"
              dataKey="deviation"
              fill="#f59e0b"
              opacity={0.6}
              isAnimationActive={false}
              name="Deviasi"
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>

      {/* Legend untuk interpretasi */}
      <div className="mt-4 text-xs text-gray-600 space-y-1">
        <p><span className="inline-block w-3 h-3 bg-blue-500 mr-2"></span>Bobot Rencana: Target persentase pekerjaan per periode</p>
        <p><span className="inline-block w-3 h-3 bg-green-500 mr-2"></span>Bobot Realisasi: Persentase aktual pekerjaan yang sudah selesai</p>
        {showDeviation && (
          <p><span className="inline-block w-3 h-3 bg-yellow-500 mr-2"></span>Deviasi: Selisih antara realisasi dan rencana (+ = ahead, - = behind)</p>
        )}
      </div>
    </div>
  )
}